import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { DataTable, DataTableColumn } from "../ui/data-table";
import { Pill } from "../ui/pill";
import { useAuth } from "../../hooks/useAuth";
import { useKamDashboard, usePartners } from "../../hooks/queries";
import type { KamDashboard } from "../../api/api";
import { partnerIconUrl } from "../../lib/partner";

type Row = KamDashboard["per_partner"][number];

// "Alle partners" overview for kam/admin: one row per partner with its
// vacature counts, from the same dashboard endpoint the KAM dashboard uses.
export default function PartnersOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useKamDashboard(user?.role === "admin");
  const { data: partners = [] } = usePartners();
  const partnerById = useMemo(() => new Map(partners.map(p => [p.id, p])), [partners]);

  const columns: DataTableColumn<Row>[] = [
    {
      key: "partner", header: "Partner", width: "minmax(200px,1.4fr)", sortValue: r => r.partner_name,
      cell: r => {
        const p = partnerById.get(r.partner_id);
        const iconUrl = p ? partnerIconUrl(p) : null;
        return (
          <div className="flex min-w-0 items-center gap-3.5">
            {iconUrl
              ? <img src={iconUrl} alt="" className="h-8 w-8 shrink-0 object-contain" />
              : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-nav bg-waves-lucht"><Building2 size={15} /></span>}
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-bold">{r.partner_name}</span>
              {p?.email_domain && <span className="truncate font-mono text-xs text-waves-muted">{p.email_domain}</span>}
            </div>
          </div>
        );
      },
    },
    { key: "open", header: "Open", width: "90px", sortValue: r => r.open, cell: r => <Pill kind="open">{r.open}</Pill> },
    { key: "hoog", header: "Hoog", width: "90px", sortValue: r => r.hoog, cell: r => r.hoog > 0 ? <Pill kind="prio-high">{r.hoog}</Pill> : <span className="font-mono text-sm text-waves-muted">0</span> },
    { key: "bemiddeling", header: "In bemiddeling", width: "130px", sortValue: r => r.in_bemiddeling, cell: r => <span className="font-mono text-sm font-semibold">{r.in_bemiddeling}</span> },
    { key: "gesloten", header: "Gesloten", width: "100px", sortValue: r => r.gesloten, cell: r => <span className="font-mono text-sm text-waves-body">{r.gesloten}</span> },
    { key: "chev", header: "", width: "20px", cell: () => <span className="text-waves-muted">›</span> },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data?.per_partner ?? []}
      rowKey={r => r.partner_id}
      onRowClick={r => navigate(`/vacatures/${r.partner_id}`)}
      defaultSort={{ key: "partner", dir: "asc" }}
      minWidth={720}
      empty={isLoading ? "Laden…" : "Nog geen partners."}
    />
  );
}
